import path from "path-posix";
import { XMLParser } from "fast-xml-parser";
import nestedProp from "nested-property";
import { decodeHTMLEntities } from "./encode.js";
import { normalisePath } from "./path.js";
var PropertyType;
(function (PropertyType) {
    PropertyType["Array"] = "array";
    PropertyType["Object"] = "object";
    PropertyType["Original"] = "original";
})(PropertyType || (PropertyType = {}));
function getParser() {
    return new XMLParser({
        removeNSPrefix: true,
        numberParseOptions: {
            hex: true,
            leadingZeros: false
        }
        // // We don't use the processors here as decoding is done manually
        // // later on - decoding early would break some path checks.
        // attributeValueProcessor: val => decodeHTMLEntities(decodeURIComponent(val)),
        // tagValueProcessor: val => decodeHTMLEntities(decodeURIComponent(val))
    });
}
function getPropertyOfType(obj, prop, type = PropertyType.Original) {
    const val = nestedProp.get(obj, prop);
    if (type === "array" && Array.isArray(val) === false) {
        return [val];
    }
    else if (type === "object" && Array.isArray(val)) {
        return val[0];
    }
    return val;
}
function normaliseResponse(response) {
    const output = Object.assign({}, response);
    nestedProp.set(output, "propstat", getPropertyOfType(output, "propstat", PropertyType.Object));
    nestedProp.set(output, "propstat.prop", getPropertyOfType(output, "propstat.prop", PropertyType.Object));
    return output;
}
function normaliseResult(result) {
    const { multistatus } = result;
    if (multistatus === "") {
        return {
            multistatus: {
                response: []
            }
        };
    }
    if (!multistatus) {
        throw new Error("Invalid response: No root multistatus found");
    }
    const output = {
        multistatus: Array.isArray(multistatus) ? multistatus[0] : multistatus
    };
    nestedProp.set(output, "multistatus.response", getPropertyOfType(output, "multistatus.response", PropertyType.Array));
    nestedProp.set(output, "multistatus.response", nestedProp.get(output, "multistatus.response").map(response => normaliseResponse(response)));
    return output;
}
/**
 * Parse an XML response from a WebDAV service,
 *  converting it to an internal DAV result
 * @param xml The raw XML string
 * @returns A parsed and processed DAV result
 */
export function parseXML(xml) {
    return new Promise(resolve => {
        const result = getParser().parse(xml);
        resolve(normaliseResult(result));
    });
}
export function prepareFileFromProps(props, rawFilename, isDetailed = false) {
    // Last modified time, raw size, item type and mime
    const { getlastmodified: lastMod = null, getcontentlength: rawSize = "0", resourcetype: resourceType = null, getcontenttype: mimeType = null, getetag: etag = null } = props;
    const type = resourceType &&
        typeof resourceType === "object" &&
        typeof resourceType.collection !== "undefined"
        ? "directory"
        : "file";
    const filename = decodeHTMLEntities(rawFilename);
    const stat = {
        filename,
        basename: path.basename(filename),
        lastmod: lastMod,
        size: parseInt(rawSize, 10),
        type,
        etag: typeof etag === "string" ? etag.replace(/"/g, "") : null
    };
    if (type === "file") {
        stat.mime = mimeType && typeof mimeType === "string" ? mimeType.split(";")[0] : "";
    }
    if (isDetailed) {
        stat.props = props;
    }
    return stat;
}
/**
 * Parse a DAV result for file stats
 * @param result The resulting DAV response
 * @param filename The filename that was stat'd
 * @param isDetailed Whether or not the raw props of
 *  the resource should be returned
 * @returns A file stat result
 */
export function parseStat(result, filename, isDetailed = false) {
    let responseItem = null;
    try {
        responseItem = result.multistatus.response[0];
    }
    catch (e) {
        /* ignore */
    }
    if (!responseItem) {
        throw new Error("Failed getting item stat: bad response");
    }
    const { propstat: { prop: props, status: statusLine } } = responseItem;
    // As defined in https://tools.ietf.org/html/rfc2068#section-6.1
    const [_, statusCodeStr, statusText] = statusLine.split(" ", 3);
    const statusCode = parseInt(statusCodeStr, 10);
    if (statusCode >= 400) {
        const err = new Error(`Invalid response: ${statusCode} ${statusText}`);
        err.status = statusCode;
        throw err;
    }
    const filePath = normalisePath(filename);
    return prepareFileFromProps(props, filePath, isDetailed);
}
/**
 * Translate a disk quota indicator to a recognised
 *  value (includes "unlimited" and "unknown")
 * @param value The quota indicator, eg. "-3"
 * @returns The value in bytes, or another indicator
 */
export function translateDiskSpace(value) {
    switch (value.toString()) {
        case "-3":
            return "unlimited";
        case "-2":
        /* falls-through */
        case "-1":
            // -1 is non-computed
            return "unknown";
        default:
            return parseInt(value, 10);
    }
}
